import { useCallback, useState } from "react";
import { API_CACHE_TTL, fetchCachedGetApi } from "@/utils/apiCache";
import { fetchAppleMusicEnrichment } from "@/utils/appleMusicEnrichment";
import { shouldIncludeSpotifyTrackForArtist } from "@/utils/spotifyArtistCredits";
import { trackIdentityKey } from "@/utils/trackIdentity";

interface SpotifyAlbum {
  id: string;
  name: string;
  release_date: string;
  images: Array<{
    url: string;
    height: number;
    width: number;
  }>;
  artists: Array<{
    id: string;
    name: string;
  }>;
}

interface SpotifyTrack {
  id: string;
  name: string;
  preview_url: string | null;
  artists: Array<{
    id: string;
    name: string;
  }>;
}

interface SpotifyAlbumDetails {
  album: {
    tracks: {
      items: SpotifyTrack[];
    };
  };
}

interface TrackData {
  id: string;
  audioUrl: string;
  title: string;
  artist: string;
  artwork?: string;
  albumName: string;
  releaseDate: string;
  source: "spotify" | "itunes" | "apple-music";
}

interface LoadTracksParams {
  albums: any;
  artistName: string;
  limit?: number;
  onTrackLoaded: (track: TrackData) => void;
}

interface UseLoadTracksWithPreviewsReturn {
  loadTracks: (params: LoadTracksParams) => Promise<number>;
  isLoading: boolean;
  loadedCount: number;
  reset: () => void;
}

/**
 * Hook to load tracks with preview URLs from an artist's albums
 * Prioritizes tracks with Spotify preview URLs and falls back to iTunes if needed
 */
function useLoadTracksWithPreviews(): UseLoadTracksWithPreviewsReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);

  /**
   * Load tracks with preview URLs from albums
   * @param params - Parameters object
   * @param params.albums - Array of album objects from Spotify
   * @param params.artistName - Artist name to verify correct tracks
   * @param params.limit - Number of tracks to load (default: 10)
   * @param params.onTrackLoaded - Callback when a track is loaded
   * @returns Promise<number> Number of tracks loaded
   */
  const loadTracks = useCallback(async ({ albums, artistName, limit = 10, onTrackLoaded }: LoadTracksParams): Promise<number> => {
    if (!albums || !artistName || !onTrackLoaded) {
      console.error("Missing required parameters for loadTracks");
      return 0;
    }

    setIsLoading(true);
    console.log(`🎵 Loading up to ${limit} tracks with preview URLs...`);

    // Sort albums by release date (newest first)
    const sortedAlbums = [...albums].sort((a, b) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime());

    let tracksLoaded = 0;
    const loadedTrackIds = new Set<string>();
    const loadedTitleKeys = new Set<string>();

    const resolvePreview = async (album: SpotifyAlbum, track: SpotifyTrack): Promise<TrackData | null> => {
      if (track.preview_url) {
        return {
          id: track.id,
          audioUrl: track.preview_url,
          title: track.name,
          artist: artistName,
          artwork: album.images?.[0]?.url,
          albumName: album.name,
          releaseDate: album.release_date,
          source: "spotify",
        };
      }
      try {
        const appleMusicData = await fetchAppleMusicEnrichment({
          artistName,
          releaseName: track.name,
          albumName: album.name,
          lookupType: "song",
          spotifyId: track.id,
        }, {
          ttlMs: API_CACHE_TTL.SIX_HOURS,
        });
        if (appleMusicData?.previewUrl) {
          return {
            id: track.id,
            audioUrl: appleMusicData.previewUrl,
            title: track.name,
            artist: artistName,
            artwork: appleMusicData.artworkUrl || album.images?.[0]?.url,
            albumName: album.name,
            releaseDate: album.release_date,
            source: appleMusicData.source,
          };
        }
      } catch (itunesError) {
        console.warn(`Could not fetch iTunes preview for ${track.name}:`, itunesError);
      }
      return null;
    };

    for (const album of sortedAlbums) {
      if (tracksLoaded >= limit) break;

      try {
        const data = await fetchCachedGetApi<SpotifyAlbumDetails>(`/.netlify/functions/SpotifyGetArtistOneAlbum`, { artist: artistName, album: album.name }, {
          ttlMs: API_CACHE_TTL.SIX_HOURS,
          shouldCache: (data) => Boolean(data?.album?.tracks?.items?.length),
        });

        const candidates = (data.album?.tracks?.items || []).filter((track) => {
          if (loadedTrackIds.has(track.id)) return false;
          if (loadedTitleKeys.has(trackIdentityKey(track.name))) return false;
          return shouldIncludeSpotifyTrackForArtist(album, track, artistName);
        });

        const uniqueCandidates: SpotifyTrack[] = [];
        const seenInAlbum = new Set<string>();
        for (const track of candidates) {
          const key = trackIdentityKey(track.name);
          if (seenInAlbum.has(key)) continue;
          seenInAlbum.add(key);
          uniqueCandidates.push(track);
        }

        const resolved = await Promise.all(uniqueCandidates.slice(0, Math.max(limit - tracksLoaded, 0) + 2).map((track) => resolvePreview(album, track)));

        for (const trackData of resolved) {
          if (tracksLoaded >= limit) break;
          if (!trackData) continue;
          const key = trackIdentityKey(trackData.title);
          if (loadedTrackIds.has(trackData.id) || loadedTitleKeys.has(key)) continue;
          onTrackLoaded(trackData);
          loadedTrackIds.add(trackData.id);
          loadedTitleKeys.add(key);
          tracksLoaded++;
          console.log(`✅ Loaded track ${tracksLoaded}/${limit}: ${trackData.title} (${trackData.source})`);
        }
      } catch (error) {
        console.error(`Error loading tracks from album ${album.name}:`, error);
      }
    }

    console.log(`✅ Successfully loaded ${tracksLoaded} tracks with preview URLs`);
    setLoadedCount((prev) => prev + tracksLoaded);
    setIsLoading(false);

    return tracksLoaded;
  }, []);

  const reset = useCallback(() => {
    setLoadedCount(0);
    setIsLoading(false);
  }, []);

  return {
    loadTracks,
    isLoading,
    loadedCount,
    reset,
  };
}

export default useLoadTracksWithPreviews;
